<template>
    <canvas id="hkcm_home_cube_canvas"></canvas>
</template>

<style>
#hkcm_home_cube_canvas {
    position: absolute;
    width: 100% !important;
    height: 100% !important;
}
</style>

<script type="module">
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { asset } from '../../helpers.js';

export default {
    data() {
        return {
            //config
            fpsLimit: 60,
            inViewport: false,
            camDistance: 5,
            defaultLookAt: new THREE.Vector3(0, 0, 2),
            shininessFront: 20,
            shininessOther: 30,
            showShadowPlane: false,

            onlyTrackOnHover: false,
            clampMouseMin: new THREE.Vector2(-1, -1),
            clampMouseMax: new THREE.Vector2(1, 1),
            trackingDepth: 0.9,
            trackingScale: 0.55,
            trackSpeed: 3,
            hoverFlip: true,
            flipSpeed: 6,

            //helper constants
            fov: 85,
            cubeSize: 1,
            refDistance: 1,
            refCanvasHeight: 800,
            rotationAmounts: [
                { axis: 'y', angle: -Math.PI / 2 },
                { axis: 'x', angle: Math.PI / 2 },
                { axis: 'y', angle: Math.PI / 2 },
                { axis: 'x', angle: -Math.PI / 2 }
            ],

            //dynamic data vars
            containerX: 0,
            containerY: 0,
            containerHalfWidth: 0.0,
            containerHalfHeight: 0.0,
            _fpsLimitInv: 0,
            _deltaSum: 0,

            mouseX: 0.0,
            relMouseX: 0.0,
            mouseY: 0.0,
            relMouseY: 0.0,
            isHovering: false,
            hoverIndex: 0,
            initialCanvasHeight: 0,

            //texture paths
            tex_tex_cube_front: null,
            tex_cube_empty: null,
            geo_cube: null
        };
    },
    computed: {
        global() {
            return window.meta?.region_configuration?.region !== 'de';
        },
        tex_cube_up_left() {
            return asset(`/hkcm/images/pcube/cube_up_left${this.global ? '_GLO' : ''}.png`, 'public');
        },
        tex_cube_up_right() {
            return asset(`/hkcm/images/pcube/cube_up_right${this.global ? '_GLO' : ''}.png`, 'public');
        },
        tex_cube_down_left() {
            return asset(`/hkcm/images/pcube/cube_down_left${this.global ? '_GLO' : ''}.png`, 'public');
        },
        tex_cube_down_right() {
            return asset(`/hkcm/images/pcube/cube_down_right${this.global ? '_GLO' : ''}.png`, 'public');
        }
    },
    mounted() {
        document.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('resize', this._onResize, false);
        window.addEventListener('scroll', this._onScroll);
        window.addEventListener('scroll', this.checkViewport);

        var hoverBoxes = document.getElementsByClassName('hkcm-hover-box');
        for (var i = 0; i < hoverBoxes.length; i++) {
            hoverBoxes[i].addEventListener('mouseenter', this._onMouseEnter);
            hoverBoxes[i].addEventListener('mouseleave', this._onMouseLeave);
        }

        this.tex_cube_front = asset('/hkcm/images/pcube/cube_front.png', 'public');
        this.tex_cube_empty = asset('/hkcm/images/pcube/cube_empty.png', 'public');
        this.geo_cube = asset('/hkcm/images/pcube/cube_geo.fbx', 'public');

        this.initCanvas();
        //this.render();
    },
    unmounted() {
        document.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('scroll', this.checkViewport);

        var hoverBoxes = document.getElementsByClassName('hkcm-hover-box');
        for (var i = 0; i < hoverBoxes.length; i++) {
            hoverBoxes[i].removeEventListener('mouseenter', this._onMouseEnter);
            hoverBoxes[i].removeEventListener('mouseleave', this._onMouseLeave);
        }

        if (this.renderID) cancelAnimationFrame(this.renderID);
    },
    methods: {
        initCanvas() {
            this.canvas = document.getElementById('hkcm_home_cube_canvas');

            var rect = this.canvas.getBoundingClientRect();
            this.containerX = rect.left;
            this.containerY = rect.top;

            this.containerHalfWidth = this.canvas.clientWidth * 0.5;
            this.containerHalfHeight = this.canvas.clientHeight * 0.5;

            this.initialCanvasHeight = this.canvas.clientHeight;

            this.isHovering = false;

            this.clock = new THREE.Clock();
            this.stats = null; //new Stats()
            if (this.stats) {
                this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
                document.body.appendChild(this.stats.dom);
            }
            this._fpsLimitInv = 1.0 / this.fpsLimit;

            this.lookTarget = new THREE.Vector3();

            this.scene = new THREE.Scene();
            //this.scene.background = new THREE.Color('transparent');

            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                antialias: true,
                alpha: true,
                powerPreference: 'high-performance'
            });
            if (this.showShadowPlane) {
                this.renderer.shadowMap.enabled = true;
                this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            }
            this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
            this.renderer.setPixelRatio(window.devicePixelRatio);
            //this.renderer.setClearColor(0x000000, 0);

            this.ambientLight = new THREE.AmbientLight(0xcfcfcf); // soft white light
            this.scene.add(this.ambientLight);

            this.directionalLight = new THREE.DirectionalLight(0xffffff, 1);
            this.directionalLight.castShadow = true;
            this.directionalLight.position.set(0, 1, 1);
            this.scene.add(this.directionalLight);

            const aspect = this.canvas.clientWidth / this.canvas.clientHeight;
            const near = 0.1;
            const far = 10000;
            this.camera = new THREE.PerspectiveCamera(this.fov, aspect, near, far);

            this.camera.position.set(0, 0, this.refDistance);

            const textureLoader = new THREE.TextureLoader();
            textureLoader.crossOrigin = '';

            var emptyTex;
            this.cubeTextureRegister = [
                textureLoader.load(this.tex_cube_front),

                textureLoader.load(this.tex_cube_up_left),
                textureLoader.load(this.tex_cube_up_right),
                textureLoader.load(this.tex_cube_down_left),
                textureLoader.load(this.tex_cube_down_right),

                (emptyTex = textureLoader.load(this.tex_cube_empty))
            ];
            const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
            this.cubeTextureRegister[0].anisotropy = maxAnisotropy;
            this.cubeTextureRegister[1].anisotropy = maxAnisotropy;
            this.cubeTextureRegister[2].anisotropy = maxAnisotropy;
            this.cubeTextureRegister[3].anisotropy = maxAnisotropy;
            this.cubeTextureRegister[4].anisotropy = maxAnisotropy;
            this.cubeTextureRegister[5].anisotropy = maxAnisotropy;
            var materials = [
                new THREE.MeshPhongMaterial({ map: emptyTex, shininess: this.shininessOther }),
                new THREE.MeshPhongMaterial({ map: emptyTex, shininess: this.shininessOther }),
                new THREE.MeshPhongMaterial({ map: emptyTex, shininess: this.shininessOther }),
                new THREE.MeshPhongMaterial({ map: emptyTex, shininess: this.shininessOther }),
                new THREE.MeshPhongMaterial({ map: this.cubeTextureRegister[0], shininess: this.shininessFront }),
                new THREE.MeshPhongMaterial({ map: this.cubeTextureRegister[0], shininess: this.shininessOther })
            ];

            //map materials to hover index
            this.cubeMaterialRegister = [materials[4], materials[0], materials[2], materials[1], materials[3]];
            this.cubeRoot = new THREE.Group();

            const widget = this;
            const fbxLoader = new FBXLoader();
            fbxLoader.load(
                this.geo_cube,
                (object) => {
                    object.traverse(function (child) {
                        if (child.isMesh) {
                            child.scale.set(widget.cubeSize, widget.cubeSize, widget.cubeSize);
                            widget.cubeRoot.remove(widget.cube);

                            child.material = materials;
                            child.castShadow = true;

                            widget.cube = child;
                            widget.cubeRoot.add(widget.cube);
                        }
                    });
                },
                (xhr) => {
                    //console.log((xhr.loaded / xhr.total) * 100 + '% loaded')
                },
                (error) => {
                    //console.log(error)
                }
            );
            const geometry = new THREE.BoxGeometry(this.cubeSize, this.cubeSize, this.cubeSize);
            this.cube = new THREE.Mesh(geometry, materials);
            if (this.showShadowPlane) {
                this.cube.castShadow = true;
                this.cube.receiveShadow = false;
            }
            this.cubeRoot.add(this.cube);
            this.scene.add(this.cubeRoot);

            if (this.showShadowPlane) {
                const geometry = new THREE.PlaneGeometry(100, 100);
                const material = new THREE.MeshStandardMaterial({ color: 0xffffff, side: THREE.DoubleSide });
                const plane = new THREE.Mesh(geometry, material);
                plane.castShadow = false;
                plane.receiveShadow = true;
                plane.position.set(0, -this.cubeSize, 0);
                plane.rotation.set(Math.PI / 2, 0, 0);
                this.scene.add(plane);
            }

            //first render size fix
            setTimeout(function () {
                widget._onResize();
                widget.handleFrame();
            }, 1000);
        },

        handleFrame() {
            if (this.stats) this.stats.begin();

            var delta = this.clock.getDelta();
            this.renderID = requestAnimationFrame(this.handleFrame);

            if (!(delta > 0) || delta > 1) {
                //skip first frame (without delta) and any spike frame from paused execution (tab change, delta > 1)
                this.renderer.render(this.scene, this.camera);
                //this.renderID = requestAnimationFrame(this.handleFrame);
                return;
            }

            //calc frame times
            this._deltaSum += delta;
            this._deltaRenderSum += delta;
            const isRenderFrame = this._deltaSum > this._fpsLimitInv;

            //execute handlers
            if (isRenderFrame) {
                const framesCount = Math.floor(this._deltaSum / this._fpsLimitInv) || 1;

                this.update(this._fpsLimitInv * framesCount); //this._deltaSum);
                this.render(this._fpsLimitInv * framesCount); //this._deltaSum);

                this._deltaSumLast = this._deltaSum;
                this._deltaSum = this._deltaSum % this._fpsLimitInv;
            }

            if (this.stats) this.stats.end();

            //this.renderID = requestAnimationFrame(this.handleFrame);
        },
        update(delta) {
            var lookTargetNew =
                this.isHovering || !this.onlyTrackOnHover ? this.getCameraPoint({ x: this.mouseX, y: this.mouseY }, this.trackingDepth, true) : this.defaultLookAt.clone();
            lookTargetNew.x = lookTargetNew.x * this.trackingScale;
            lookTargetNew.y = lookTargetNew.y * this.trackingScale;
            this.lookTarget.lerp(lookTargetNew, this.trackSpeed * delta);

            this.directionalLight.position.set(this.lookTarget.x, this.lookTarget.y, 1);

            this.cubeRoot.lookAt(this.lookTarget);

            if (this.hoverFlip) this.rotateCubeToActiveSide(this.hoverIndex, this.flipSpeed * delta);
        },

        render(delta) {
            this.renderer.render(this.scene, this.camera);
        },

        getCameraPoint(docPos, depth, applyClamp) {
            var relPos = new THREE.Vector2((docPos.x - this.containerX) / this.containerHalfWidth - 1, -(docPos.y - this.containerY) / this.containerHalfHeight + 1);

            if (applyClamp) relPos.clamp(this.clampMouseMin, this.clampMouseMax);

            var pos = new THREE.Vector3(relPos.x, relPos.y, depth);
            pos = pos.unproject(this.camera);
            return pos;
        },

        rotateCubeToActiveSide(activeSideIndex, speed) {
            var targetRotation = new THREE.Euler(0, 0, 0);
            if (activeSideIndex !== 0) {
                const activeRotation = this.rotationAmounts[activeSideIndex - 1];
                targetRotation[activeRotation.axis] = activeRotation.angle;
            }

            this.cube.rotation.set(
                THREE.MathUtils.lerp(this.cube.rotation.x, targetRotation.x, speed),
                THREE.MathUtils.lerp(this.cube.rotation.y, targetRotation.y, speed),
                THREE.MathUtils.lerp(this.cube.rotation.z, targetRotation.z, speed)
            );
        },

        _onMouseMove(event) {
            this.mouseX = event.clientX;
            this.mouseY = event.clientY;
        },

        _onMouseEnter(event) {
            this.isHovering = true;
            this.hoverIndex = 0;

            if (event.target.classList.contains('hkcm-hover-box-up-left')) {
                this.hoverIndex = 1;
            } else if (event.target.classList.contains('hkcm-hover-box-up-right')) {
                this.hoverIndex = 2;
            } else if (event.target.classList.contains('hkcm-hover-box-down-left')) {
                this.hoverIndex = 3;
            } else if (event.target.classList.contains('hkcm-hover-box-down-right')) {
                this.hoverIndex = 4;
            } else {
                this.isHovering = false;
            }

            if (this.isHovering) this.cubeMaterialRegister[this.hoverFlip ? this.hoverIndex : 0].map = this.cubeTextureRegister[this.hoverIndex];
        },

        _onMouseLeave(event) {
            this.isHovering = false;
            this.cubeMaterialRegister[this.hoverFlip ? this.hoverIndex : 0].map = this.hoverFlip ? this.cubeTextureRegister[5] : this.cubeTextureRegister[0];
            this.hoverIndex = 0;
        },

        _onScroll(event) {
            var rect = this.canvas.getBoundingClientRect();
            this.containerX = rect.left;
            this.containerY = rect.top;
        },

        _onResize(event) {
            var rect = this.canvas.getBoundingClientRect();
            this.containerX = rect.left;
            this.containerY = rect.top;

            this.containerHalfWidth = this.canvas.clientWidth * 0.5;
            this.containerHalfHeight = this.canvas.clientHeight * 0.5;

            this.camera.aspect = this.canvas.clientWidth / this.canvas.clientHeight;

            this.fov = 2 * Math.atan(this.cubeSize / 2 / (this.refDistance * (this.canvas.clientHeight / this.refCanvasHeight))) * (180 / Math.PI);

            this.camera.fov = (this.fov * this.canvas.clientHeight) / this.refCanvasHeight;
            this.camera.updateProjectionMatrix();

            this.camera.position.z = this.camDistance * (this.canvas.clientHeight / this.refCanvasHeight);

            this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
        },
        checkViewport() {
            let currentViewport = this.isInViewport(this.canvas);
            if (this.inViewport != currentViewport) {
                this.inViewport = currentViewport;
                let fps = this.inViewport ? this.fpsLimit : 0;
                this._fpsLimitInv = 1.0 / fps;
                // console.log('Cube fps: ' + fps);
            }
        },
        isInViewport(element) {
            const rect = element.getBoundingClientRect();
            return rect.bottom >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight);
        }
    }
};
</script>
